// Shared API types for Backoffice App

export type VatCode = 'AT0' | 'AT4_9' | 'AT10' | 'AT13' | 'AT20'

export interface VolumeTier {
  minQty: number
  amountCents: number
}

export interface RestaurantVolumeTier {
  restaurantId: number
  minQty: number
  amountCents: number
}

export interface Product {
  id: number
  tenantId: number
  sku: string
  name: string
  publicName?: string
  vatCode: VatCode
  active: boolean
  deletedAt?: string
  barcode?: string
  categoryCode?: string // Category code for filtering
  currentPrice?: number // Price in cents
  openPrice?: boolean
  /** Whether the customer may choose warm/cold for this product (default true). */
  temperatureChoice?: boolean
  /**
   * The package base-unit override (CHG-2026-09-webbill-delivery-import), or absent when the rule
   * decides (the smallest synced unit). paydesk-owned: the article sync never writes it.
   */
  baseUnitLevel?: number
  // Only present when the request was scoped to a restaurant: whether the product is assigned to it.
  // A product that is not assigned never reaches the POS, even when it has a price.
  assignedHere?: boolean
  /**
   * CHG-2026-09-duplicate-product-merge: present when this product is a merged duplicate — the
   * SKU of its primary. Present on both the list and the detail response.
   */
  mergedIntoSku?: string
  /**
   * CHG-2026-09-pos-layout-stock-and-menu-diff: in-store available quantity at the requested
   * restaurant, present only when the lookup was asked for stock. Absent means "not tracked here",
   * which is not the same as zero — never render an absent value as 0.
   */
  availableQty?: number
  /**
   * CHG-2026-09-duplicate-product-merge: present on the detail response only, when this product
   * has one or more duplicates merged into it.
   */
  duplicateSkus?: Array<string>
  /**
   * CHG-2026-09-stock-usage-and-price-twins (optional until the backoffice screens land):
   * a dish has its own stock usage list; a variant names its source and the share of the
   * source's stock one sale uses; `stockUnit` is the base unit as shown ("Stk" by default),
   * `stockUnitLabel` the paydesk display name when one is set.
   */
  isDish?: boolean
  stockTracked?: boolean
  fractionalCounting?: boolean
  stockUnit?: string
  stockUnitLabel?: string
  variantSourceSku?: string
  variantShare?: number
  /** On a variant: inactive because its source is inactive; it resumes with the source. */
  pausedBySource?: boolean
  variantSkus?: Array<string>
  /**
   * CHG-2026-09-stock-usage-and-price-twins (D8, task 7.4): present on a lookup with
   * `includeStock` for derived products only (dish, variant, not-tracked article). A plain
   * article carries none of these and keeps its own `availableQty`.
   */
  stockKind?: StockKind
  /** No tracked part, or the restaurant does not track inventory: never sold out. */
  unlimited?: boolean
  inStoreQty?: number
  onlineQty?: number
  limitingSku?: string
  limitingName?: string
  inStoreLimitingSku?: string
  inStoreLimitingName?: string
  parts?: Array<DerivedStockPart>
}

/** What kind of stock a product holds (CHG-2026-09-stock-usage-and-price-twins). */
export type StockKind = 'article' | 'dish' | 'variant' | 'not_tracked'

/** One tracked part of a derived product's availability reading. */
export interface DerivedStockPart {
  sku: string
  name?: string
  qtyPerUnit: number
  availableQty: number
  sellableQty: number
  inStoreQty: number
  onlineQty: number
  /** The part's base unit as shown ("Stk" by default); phase B4. */
  unit?: string
  fractionalCounting?: boolean
}

/** Response of `GET /admin/products/lookup` — resolves an explicit set of SKUs. */
export interface ProductLookupResponse {
  items: Array<Product>
  /** Requested SKUs that do not exist at all, as opposed to existing-but-unavailable ones. */
  missing: Array<string>
}

export interface ProductListResponse {
  items: Array<Product>
  total: number
  page: number
  /** Effective page size — the server clamps oversized requests, so this may be below the request. */
  pageSize: number
}

export interface ProductAssignment {
  restaurantId: number
  restaurantName: string
  assigned: boolean
  assignedAt?: string
  assignedBy?: string
}

export interface ProductPrice {
  productId: number
  restaurantId: number
  amountCents: number
  currency: string
  updatedAt: string
  updatedBy?: string
}

export type RestaurantVisibility = 'PUBLIC' | 'INTERNAL'

export interface Restaurant {
  id: number
  tenantId: number
  name: string
  code: string
  address?: Record<string, unknown>
  timezone?: string
  onlineOrderingEnabled?: boolean
  defaultSafetyBufferQty?: number
  inventoryTrackingEnabled?: boolean
  /**
   * Whether the expected quantity is shown while counting stock at this location. Off means a
   * blind count: the assortment payload omits expected quantities entirely.
   */
  inventoryCountShowExpected?: boolean
  companyProfile?: CompanyProfile
  visibility?: RestaurantVisibility
  enrollmentCode?: string | null
  /**
   * The webbill pseudo-GLN this store's delivery notes are addressed to. Null means the store
   * receives no supplier imports (CHG-2026-09-webbill-delivery-import).
   */
  webbillReceiverRef?: string | null
}

// Scheduling
export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export interface RestaurantHoursDay {
  closed: boolean
  open?: string
  close?: string
}
export type RestaurantHours = Record<DayKey, RestaurantHoursDay>

export interface SlotConfig {
  slotDurationMinutes: number
  serviceStartLocal: string
  serviceEndLocal: string
}

export interface LeadWindowRule {
  untilLocal?: string
  afterLocal?: string
  minAdvanceMinutes: number
}

export interface OrderingWindow {
  opensLocal: string
  closesLocal: string | null
}

// Per-window override for a single date: either closed or replacement times
export interface SchedulingExceptionWindow {
  closed?: boolean
  open?: string
  close?: string
}

export interface SchedulingExceptionOrdering {
  closed?: boolean
  opensLocal?: string
  closesLocal?: string | null
}

export interface SchedulingException {
  date: string
  unmanned?: SchedulingExceptionWindow
  staffed?: SchedulingExceptionWindow
  ordering?: SchedulingExceptionOrdering
  pickup?: SchedulingExceptionWindow
}

export interface RestaurantScheduling {
  unmannedHours: RestaurantHours
  staffedHours: RestaurantHours
  ordering: OrderingWindow
  slots: SlotConfig
  leadWindows: Array<LeadWindowRule>
  exceptions: Array<SchedulingException>
}

export interface SchedulingPreviewNow {
  nowLocal: string
  orderable: boolean
  reasonCode: string | null
  minAdvanceMinutes: number
  lastOrderableLocal: string | null
  earliestPickupLocal: string | null
  latestPickupLocal: string | null
}

export interface SchedulingPreviewResponse {
  date: string
  exceptionApplied: boolean
  unmanned: RestaurantHoursDay
  staffed: RestaurantHoursDay
  ordering: {
    closed: boolean
    opensLocal?: string
    closesLocal?: string | null
  }
  pickup: {
    closed: boolean
    durationMinutes?: number
    serviceStartLocal?: string
    serviceEndLocal?: string
  }
  leadWindows: Array<LeadWindowRule>
  now: SchedulingPreviewNow
}

export interface EdgeSettingsEntry {
  key: string
  value: unknown
}

export interface PostalAddress {
  street?: string
  postalCode?: string
  city?: string
  country?: string
}

export interface CompanyProfile {
  legal?: {
    name?: string
    uid?: string
    registerId?: string
    owner?: string
    address?: PostalAddress
  }
  location?: {
    address?: PostalAddress
  }
  contact?: {
    phone?: string
    email?: string
    websiteUrl?: string
  }
  media?: {
    thumbnailUrl?: string
    headerImageUrl?: string
  }
  receipt?: {
    hoursText?: string
  }
  /** URL slug for printed store deep links (QR codes). Unique per tenant, server-validated. */
  urlSlug?: string
}

export interface EdgeKioskConfig {
  stationId: string
  zone: string
  name?: string
}

export interface EdgeDeviceConfig {
  id?: string
  name?: string
  role: string
  protocol?: string
  host?: string
  port?: number
  zones?: Array<string>
  allowedKiosks?: Array<string>
  priority?: number
  settings?: EdgeDeviceSettings
}

export interface EdgeDeviceSettings {
  cardReceiptPrint?: 'ecr' | 'terminal'
}

export type EdgeRoutingRole =
  | 'payment.card'
  | 'payment.cash_recycler'
  | 'printer.receipt'
  | 'printer.kitchen.hot'

export interface EdgeRoutingStationAssignment {
  stationId: string
  role: EdgeRoutingRole
  deviceId: string
}

export interface EdgeRoutingZoneDefault {
  zone: string
  role: EdgeRoutingRole
  deviceId: string
}

export interface EdgeRoutingFallback {
  role: EdgeRoutingRole
  deviceId: string
}

export interface EdgeDeviceRoutingSettings {
  stationAssignments?: Array<EdgeRoutingStationAssignment>
  zoneDefaults?: Array<EdgeRoutingZoneDefault>
  fallbacks?: Array<EdgeRoutingFallback>
}

export interface RestaurantEdgeSettings {
  settings: Array<EdgeSettingsEntry>
  devices: Array<EdgeDeviceConfig>
}

export interface RestaurantEdgeSettingsPatch {
  settingsUpsert?: Array<EdgeSettingsEntry>
  settingsDelete?: Array<string>
  devicesUpsert?: Array<EdgeDeviceConfig>
  devicesDelete?: Array<string>
}

export interface EdgeHub {
  id: number
  tenantId: number
  restaurantId: number
  name: string
  status: 'online' | 'offline'
  currentBundleVersion?: number
  lastSeenAt?: string
}

export interface OverviewResponse {
  edges: {
    total: number
    online: number
    offline: number
  }
  bundleVersions: Array<{
    restaurantId: number
    restaurantName: string
    latestVersion: number | null
    publishedAt: string | null
  }>
}

export interface MeResponse {
  email: string
  roles: Array<string>
  tenantId?: number
  restaurantIds?: Array<number>
  locale?: string
}

export interface UpdatePreferencesRequest {
  locale?: string
}

export interface UpdatePreferencesResponse {
  locale: string
}

export interface AdminUser {
  id: number
  email: string
  roles: Array<string>
  disabledAt?: string | null
  createdAt?: string | null
  firstName?: string | null
  lastName?: string | null
  tenantId: number
  restaurantIds?: Array<number>
}

export interface UsersListResponse {
  items: Array<AdminUser>
  total: number
  page: number
  pageSize: number
}

export interface CreateUserRequest {
  email: string
  password: string
  roles: Array<string>
  firstName?: string
  lastName?: string
}

export interface UpdateUserRequest {
  email?: string
  disabled?: boolean
}

export interface UpdateUserRolesRequest {
  roles: Array<string>
}

export interface RolePermissionsResponse {
  actions: Array<string>
  roles: Array<{
    role: string
    actions: Array<string>
    locked?: boolean
  }>
}

export interface UpdateRolePermissionsRequest {
  actions: Array<string>
}

export interface AuthResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface CreateProductRequest {
  sku?: string
  name: string
  publicName?: string
  vatCode: VatCode
  active: boolean
  temperatureChoice?: boolean
  barcode?: string
}

export interface CreateProductResponse {
  ok: boolean
  id: number
  sku: string
  bundlesPublished?: number
}

export interface UpdateProductRequest {
  name?: string
  publicName?: string
  vatCode?: VatCode
  active?: boolean
  temperatureChoice?: boolean
  barcode?: string
  /**
   * CHG-2026-09-stock-usage-and-price-twins (D4): a variant's share of its source's stock
   * usage, > 0 with at most 3 decimals. Only ever sent for a variant, and only when changed.
   */
  share?: number
  /**
   * The package base-unit override (CHG-2026-09-webbill-delivery-import): the level paydesk scans
   * and counts stock in. paydesk-owned, so it is editable on imported products too; send 0 to
   * clear it and fall back to the derived base (the smallest unit).
   */
  baseUnitLevel?: number
}

export interface PricingImportRequest {
  restaurantId: number
  items: Array<{
    sku: string
    priceGrossCents: number
  }>
}

// Categories
export interface Category {
  id: number
  tenantId: number
  code: string
  name: string
  /** null = a main group. A subgroup points at its main group (CHG-2026-09-product-category-hierarchy). */
  parentId: number | null
  sortOrder: number
  active: boolean
}

export interface UpdateCategoryRequest {
  name: string
  /** null means "no parent", not "unchanged" — removing a parent has to be expressible. */
  parentId: number | null
  sortOrder?: number
  active?: boolean
}

/** One article in the collector category that is worth assigning: sold in the period, or priced. */
export interface UnassignedProduct {
  sku: string
  name: string
  sold: boolean
  revenueCents: number
  units: number
  priceCents: number | null
  /** Last sale across all time, not just the period — null when it never sold. */
  lastSoldAt: string | null
}

export interface UnassignedProductsResponse {
  collectorCategoryId: number | null
  from: string
  to: string
  total: number
  soldCount: number
  pricedCount: number
  items: Array<UnassignedProduct>
}

export type UnassignedFilter = 'all' | 'sold' | 'priced'

export interface CreateCategoryRequest {
  code: string
  name: string
  /**
   * null creates a main group; an id creates the group already hung under that main group, so
   * "+ Untergruppe" is one save rather than a create followed by an edit.
   */
  parentId?: number | null
  sortOrder?: number
  active?: boolean
}

// Menus (single-day model: one master per restaurant, one daily per date)
export type MenuType = 'master' | 'daily'
export type MenuStatus = 'draft' | 'published'
export type MenuCreateMode = 'empty' | 'fromMaster' | 'fromDate'

export interface Menu {
  id: number
  tenantId: number
  restaurantId: number
  name: string
  type: MenuType
  status: MenuStatus
  menuDate?: string | null // ISO date; null for master
  createdAt?: string
  updatedAt?: string
  deletedAt?: string | null
}

// Menu Sections
export interface MenuSection {
  id: number
  menuId?: number
  name: string
  sortOrder: number
  categoryId?: number
  /** "Vor Ort": the /tisch view shows exactly the sections carrying this flag, nothing else. */
  dineInOnly?: boolean
  /** "Tagesmenü": defines the Tagesgerichte print scope only — no effect on visibility. */
  dailySpecial?: boolean
  /** "Speisekarte": visible everywhere except /tisch. Missing means visible — fail open. */
  menuCard?: boolean
  deletedAt?: string
}

// Menu Items
export interface MenuItem {
  menuSectionId?: number
  productId: number
  sku: string
  baseName: string
  /** The internal (webbill) name, shown alongside baseName so a manager can tell them apart. */
  internalName?: string
  overrideName?: string
  sortOrder: number
  active: boolean
  priceMissing?: boolean
  thumbUrl?: string
  imageUrl?: string
  /** The product's temperature-choice flag, so the row can toggle it without a second fetch. */
  temperatureChoice?: boolean
  sellableQty?: number
  /**
   * The terms behind `sellableQty` (CHG-2026-09-menu-editor-redesign), so the planner can show
   * the calculation rather than only its result.
   *
   * All three are `null` — not `0` — when the SKU has no inventory row at all: "not tracked" and
   * "tracked, and empty" are different things to someone planning a menu.
   */
  availableQty?: number | null
  reservedQty?: number | null
  safetyBufferQty?: number | null
  /**
   * Announced, unbooked, counting deliveries expected on **this menu's day** — not today, which
   * is what `incomingQty` means on every other endpoint (RULE-CLOUD-INV-010). Already contained
   * in `sellableQty`. Null on a master menu, which has no day to plan against.
   */
  incomingForDateQty?: number | null
  /** Announced for the menu's day but switched off for pre-orders, so deliberately NOT in `sellableQty`. */
  incomingNotCountingQty?: number | null
  /** The documents behind the two quantities above, so the row's chip can name and link them. */
  incomingDeliveries?: Array<MenuItemDelivery> | null
  /**
   * CHG-2026-09-stock-usage-and-price-twins (D8, task 7.3): present on a dish, variant or
   * not-tracked row only. `sellableQty` is then the online reading derived from the tracked parts
   * (null when `unlimited`), and the limiting part names what runs out first. The stock terms
   * above stay null — such a product holds no stock of its own.
   */
  stockKind?: StockKind
  unlimited?: boolean
  limitingSku?: string
  limitingName?: string
}

/** One announced delivery contributing to a menu item's incoming quantity. */
export interface MenuItemDelivery {
  id: string
  supplier: string
  countsForPreorders: boolean
}

// Menu Contents (editor view)
export interface MenuContents {
  menu: Menu
  sections: Array<{
    id: number
    name: string
    sortOrder: number
    categoryId?: number
    dineInOnly?: boolean
    dailySpecial?: boolean
    menuCard?: boolean
    items: Array<MenuItem>
  }>
}

/** Response of POST /api/v0/admin/menus/{id}/print-link. */
export interface MenuPrintLink {
  /** Order-app URL that renders and prints this day's Karte. */
  url: string
  /** ISO date of the menu the link prints. */
  date: string
  /** Lifetime of the link's preview token — it stops working after this. */
  expiresInSeconds: number
}

// Menu Request/Response types
export interface CreateDailyMenuRequest {
  restaurantId: number
  type: 'daily'
  menuDate: string // ISO date
  mode: MenuCreateMode
  fromDate?: string // ISO date, for mode=fromDate
}

export interface CreateMasterMenuRequest {
  restaurantId: number
  type: 'master'
}

export interface DailyCreateResult {
  ok: boolean
  id: number
  copied: number
  skipped: Array<{ sku: string; reason: string }>
}

/** 409 payload when a daily already exists for the date. */
export interface DailyExistsResult {
  ok: false
  code: 'daily_exists'
  existingMenuId: number
}

export interface UpdateMenuRequest {
  name?: string
}

export interface GeneratePosPageResponse {
  ok: boolean
  layout: unknown
}

export interface PublishMenuResponse {
  ok: boolean
  code?: 'missing_prices'
  items?: Array<{ sku: string }>
}

export interface CreateSectionRequest {
  name: string
  sortOrder?: number
  categoryId?: number
  dineInOnly?: boolean
  dailySpecial?: boolean
  menuCard?: boolean
}

export interface UpdateSectionRequest {
  name?: string
  sortOrder?: number
  dineInOnly?: boolean
  dailySpecial?: boolean
  menuCard?: boolean
}

export interface AddItemRequest {
  productId: number
  sortOrder?: number
  overrideName?: string
}

export interface UpdateItemRequest {
  sortOrder?: number
  overrideName?: string
  active?: boolean
}

export interface AddItemResponse {
  ok: boolean
  autoAssigned: boolean
  priceMissing: boolean
}

export interface BulkAddItemsResponse {
  ok: boolean
  added: number
  autoAssignedCount: number
  missingPriceCount: number
}

// POS Layouts
export interface PosLayout {
  version: number
  pages: Array<PosLayoutPage>
  openPriceButtons?: Array<string>
  /** Self-checkout add-on articles (e.g. Besteck, Tragetasche), offered in this order. */
  checkoutAddonSkus?: Array<string>
}

export interface PosLayoutPage {
  id: string
  name: string
  grid: {
    columns: number
    rows: number
  }
  tiles: Array<PosLayoutTile>
}

export interface PosLayoutTile {
  id: string
  sku: string
  row: number
  col: number
  color: string
  label?: string
}

export interface PosLayoutResponse {
  restaurantId: number
  layout: PosLayout
}

export interface PosLayoutSaveResponse {
  ok: boolean
  bundleId: number
  version: number
}

// Staff
export interface StaffMember {
  id: string
  displayName: string
  role: string
  status: 'active' | 'disabled'
  restaurantIds: Array<number>
  createdAt?: string
  updatedAt?: string
}

export interface StaffListResponse {
  items: Array<StaffMember>
}

export interface StaffCreateRequest {
  displayName: string
  role?: string
  pin: string
  status?: 'active' | 'disabled'
  restaurantIds?: Array<number>
}

export interface StaffUpdateRequest {
  displayName?: string
  role?: string
  pin?: string
  status?: 'active' | 'disabled'
  restaurantIds?: Array<number>
}

export interface StaffSaveResponse {
  ok: boolean
  id?: string
  updated?: number
  staff?: StaffMember
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code?: string,
    message?: string,
    public data?: unknown,
  ) {
    super(message || `HTTP ${status}`)
    this.name = 'ApiError'
  }
}

// Transactions (POS + Mobile)
export type TransactionChannel = 'pos' | 'mobile'

export interface TransactionRow {
  channel: TransactionChannel
  id: string
  restaurantId: number
  occurredAt: string
  currency: string
  totalGrossCents: number
  paymentMethod?: string | null
  status: string
  operatorId?: string | null
  stationId?: string | null
  receiptNo?: string | null
}

export interface TransactionsListResponse {
  items: Array<TransactionRow>
  total: number
  limit: number
  offset: number
  summary?: {
    count: number
    grossTotalCents: number
    byPaymentMethod: Array<{
      paymentMethod: string | null
      count: number
      totalGrossCents: number
    }>
  }
}

export interface TransactionDetailItem {
  sku: string
  name: string
  qty: number
  unitGrossCents: number
  lineGrossCents: number
  vatCode?: string | null
}

export interface TransactionDetail {
  channel: TransactionChannel
  id: string
  restaurantId: number
  occurredAt: string
  currency: string
  totalGrossCents: number
  paymentMethod?: string | null
  status: string
  operatorId?: string | null
  items: Array<TransactionDetailItem>
  receiptNo?: string | null
  stationId?: string | null
}

export interface VoucherTypeConfig {
  id: number
  name: string
  amountsCents: Array<number>
  active?: boolean
  sortOrder?: number
}

export interface VoucherTypeInput {
  name: string
  amountsCents: Array<number>
  active?: boolean
  sortOrder?: number
}

export interface VoucherConfigResponse {
  restaurantId: number
  types: Array<VoucherTypeConfig>
}

export interface VoucherConfigSaveResponse {
  ok: boolean
  bundleId?: number
  version?: number
}

export interface PosVoidReason {
  code: string
  label: string
  active?: boolean
  sortOrder?: number
}

export interface PosVoidReasonInput {
  code: string
  label: string
  active?: boolean
  sortOrder?: number
}

export interface PosVoidReasonsResponse {
  restaurantId: number
  items: Array<PosVoidReason>
}

export interface PosVoidReasonsSaveResponse {
  ok: boolean
  bundleId?: number
  version?: number
}

export interface EntnahmeReason {
  code: string
  label: string
  active?: boolean
  sortOrder?: number
}

export interface EntnahmeReasonInput {
  code: string
  label: string
  active?: boolean
  sortOrder?: number
}

export interface EntnahmeReasonsResponse {
  restaurantId: number
  items: Array<EntnahmeReason>
}

export interface EntnahmeReasonsSaveResponse {
  ok: boolean
  bundleId?: number
  version?: number
}
